/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';

// @mui material components
import Icon from '@mui/material/Icon';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import ListItemButton from '@mui/material/ListItemButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Badge from '@mui/material/Badge';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';

function PosApprove({ reload, count, setBlockScan }) {
  const mod = 'pos_return';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [record, setRecord] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notify, setNotify] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const btnRef = useRef(null);

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );

    setTimeout(function () {
      setNotify(null);
    }, 5000);
  };

  const view = () => {
    setLoading(true);

    FetchData('POST', `/${mod}/view`, app.token, {
      lang: app.lang,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setRecord(result.data);
          setModalOpen(true);
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const begin = (item) => {
    setLoading(true);

    FetchData('OPTIONS', `/pointofsale/return`, app.token, {
      lang: app.lang,
      id: item.id,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          reload();
          setModalOpen(false);
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const close = () => {
    setModalOpen(false);
    setBlockScan(false);

    setTimeout(function () {
      btnRef.current.blur();
    }, 100);
  };

  const reKeep = (item) => {
    setLoading(true);

    FetchData('OPTIONS', `/pointofsale/rekeep`, app.token, {
      lang: app.lang,
      id: item.id,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          reload();
          setModalOpen(false);
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const cancelApprove = (item) => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('OPTIONS', `/${mod}/cancelapprove`, app.token, {
          id: item.id,
          status: 'cancel',
        })
          .then((result) => {
            if (result.status && result.status === 'ok') {
              setRecord(result.data);
              showNotify(t(`alert.editcomplete`), 'success');
            } else if (!result.status) {
              showNotify(t('alert.server_timeout'), 'error');
            } else {
              showNotify(t(`alert.${result.msg}`), 'error');
            }
          })
          .catch((error) => {
            showNotify(t('alert.network_timeout'), 'error');
          })
          .finally(() => {
            setLoading(false);
          });
      }
    });
  };

  const btnOpen = (item) => {
    return (
      <Tooltip title='open'>
        <IconButton
          aria-label='reply'
          color='info'
          sx={{ marginLeft: 'auto' }}
          onClick={() => begin(item)}
          disabled={item.status !== 'approve'}
        >
          <Icon>save_alt</Icon>
        </IconButton>
      </Tooltip>
    );
  };

  const btnDel = (item) => {
    return (
      <Tooltip title='delete'>
        <IconButton
          aria-label='delete'
          color='error'
          sx={{ marginLeft: 'auto' }}
          onClick={() => cancelApprove(item)}
        >
          <Icon>delete</Icon>
        </IconButton>
      </Tooltip>
    );
  };

  const renderList2 = () => {
    return (
      <MDBox>
        <List component='nav'>
          {record.map((row) => (
            <ListItemButton alignItems='flex-start' key={row.id} onClick={() => begin(row)}>
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: 'red' }}>
                  <i className='far fa-clipboard-check' />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <MDBox>
                    <span>{row.code}</span>{' '}
                    <code className={`color-${row.status_color}`}>{row.status_name}</code>
                  </MDBox>
                }
                secondary={
                  <Typography
                    component='span'
                    variant='body2'
                    sx={{ color: 'text.primary', display: 'inline' }}
                  >
                    {row.user_name}, {Func.dateTimeDisplay(row.updated_at)}
                  </Typography>
                }
              />
            </ListItemButton>
          ))}
        </List>
      </MDBox>
    );
  };

  const renderList = () => {
    return (
      <MDBox>
        <TableContainer sx={{ boxShadow: 0, height: 350 }}>
          <Table aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell align='center'>ลำดับ</TableCell>
                <TableCell align='center'>รหัส</TableCell>
                <TableCell align='center'>ผู้บันทึก</TableCell>
                <TableCell align='center'>เวลา</TableCell>
                <TableCell>&nbsp;</TableCell>
                <TableCell>&nbsp;</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {record.map((row, i) => (
                <TableRow key={row.id} hover>
                  <TableCell align='center'>{i + 1}</TableCell>
                  <TableCell align='center'>{row.code}</TableCell>
                  <TableCell align='center'>{row.user_name}</TableCell>
                  <TableCell align='center'>{Func.dateTimeDisplay(row.updated_at)}</TableCell>
                  <TableCell align='center'>{btnOpen(row)}</TableCell>
                  <TableCell align='center'>{btnDel(row)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    );
  };

  const renderOpen = () => {
    return (
      <Dialog
        open={modalOpen}
        maxWidth='md'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={close}
      >
        <DialogTitle>
          รายการอนุมัติ
          <IconButton
            aria-label='close'
            onClick={close}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2, overflow: 'hidden' }}>
          {renderList()}
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <>
      <Tooltip title='รายการอนุมัติ' sx={{ mr: 4 }}>
        <Badge badgeContent={count} color='primary'>
          <IconButton size='small' ref={btnRef} onClick={view}>
            <i className='fad fa-clipboard-check' />
          </IconButton>
        </Badge>
      </Tooltip>

      {notify}
      {renderOpen()}
    </>
  );
}

PosApprove.propTypes = {
  reload: PropTypes.func.isRequired,
  setBlockScan: PropTypes.func.isRequired,
  count: PropTypes.number.isRequired,
};

export default PosApprove;
